import { motion, useReducedMotion } from "framer-motion";
import { type SyntheticEvent, useRef, useEffect } from "react";
import { track } from "@/lib/analytics";
import { Check } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1] as const;

const POINTS = [
  "Part du contexte de chaque client",
  "Une structure claire, 100 % éditable",
  "Votre expertise reste au centre",
];

const ProgramDemo = ({ compact = false }: { compact?: boolean }) => {
  const progressEvents = useRef(new Set<number>());
  const videoRef = useRef<HTMLVideoElement>(null);
  const shouldReduce = useReducedMotion();

  // La vidéo (2,7 Mo) ne se télécharge qu'à l'approche de la section :
  // preload="none" + lecture déclenchée à l'intersection. prefers-reduced-motion
  // désactive l'autoplay (l'utilisateur garde les contrôles).
  useEffect(() => {
    const video = videoRef.current;
    if (!video || shouldReduce) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.35 }
    );
    io.observe(video);
    return () => io.disconnect();
  }, [shouldReduce]);

  function handleVideoProgress(event: SyntheticEvent<HTMLVideoElement>) {
    const video = event.currentTarget;
    if (!video.duration) return;
    const percent = Math.floor((video.currentTime / video.duration) * 100);
    for (const milestone of [25, 50, 75]) {
      if (percent >= milestone && !progressEvents.current.has(milestone)) {
        progressEvents.current.add(milestone);
        track("demo_video_progressed", { video: "program_generation", percent: milestone });
      }
    }
  }

  if (compact) return <video ref={videoRef} className="block aspect-video w-full bg-[#142033] object-contain" src="/demo_programme_v3_light.mp4" poster="/demo_poster.jpg" muted loop controls playsInline preload="none" onPlay={() => track("demo_video_played", { video: "program_generation", location: "features" })} onTimeUpdate={handleVideoProgress} aria-label="Démonstration : créer et personnaliser un programme sportif" />;

  return (
    <section id="demo-programme" className="program-screening relative overflow-hidden py-16 md:py-24">

      <div className="container relative z-10">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: EASE }}
            className="screening-intro"
          >

            <p className="story-eyebrow">02 — Du brief au programme</p>
            <h2 className="landing-section-title text-3xl font-extrabold leading-[1.1] tracking-tight text-[#0e1220] md:text-5xl">
              Un programme prêt en{" "}
              <span className="text-[#2563EB]">quelques minutes.</span>
            </h2>

            <p className="mt-5 max-w-xl text-base leading-relaxed text-[#0e1220]/60">
              CoachOS part du contexte client pour préparer une structure claire. Vous ajustez, vous validez, vous gardez votre expertise au centre.
            </p>

            <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-3 text-left">
              {POINTS.map((p) => (
                <li key={p} className="flex items-center gap-3 text-[15px] text-[#0e1220]/80">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#2563EB]/15">
                    <Check className="h-3.5 w-3.5 text-[#2563EB]" />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.4, ease: EASE }}
            className="screening-video relative mt-10"
          >
            {/* Mobile : vidéo nue pleine largeur, bord à bord — cadre fenêtre dès md */}
            <div className="relative -mx-4 overflow-hidden md:mx-0 md:rounded-2xl md:border md:border-[#e7e9f1] md:ring-1 md:ring-[#e7e9f1] md:shadow-[0_30px_60px_-25px_rgba(14,18,32,0.25)]" style={{ background: "#f4f5fa" }}>
              {/* barre façon macOS : desktop uniquement */}
              <div className="hidden items-center justify-between border-b border-[#e7e9f1] bg-[#eef0f4] px-4 py-3 md:flex">
                <div className="flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
                  <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
                  <span className="h-3 w-3 rounded-full bg-[#28c840]" />
                </div>
                <span className="text-[11px] font-semibold text-[#0e1220]/60">Création d'un programme haut du corps</span>
                <span className="w-12" />
              </div>

              <video
                ref={videoRef}
                className="block aspect-video w-full bg-[#f4f5fa] object-contain"
                src="/demo_programme_v3_light.mp4"
                poster="/demo_poster.jpg"
                muted
                loop
                playsInline
                controls
                preload="none"
                onPlay={() => track("demo_video_played", { video: "program_generation", location: "program_demo" })}
                onTimeUpdate={handleVideoProgress}
                aria-label="Démonstration CoachOS : générer puis personnaliser un programme sportif avec l'agent IA"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProgramDemo;
